import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

/**
 * Signed-URL resolution for private storage buckets.
 *
 * The DB stores storage PATHS ("userid/avatar-123.png"); rendering needs a
 * short-lived signed URL now that the buckets are private. Three value shapes
 * must resolve, because rows written before the buckets flipped (and
 * admin-pasted external links) are still out there:
 *
 *   - storage path                       -> createSignedUrl
 *   - legacy public URL for this bucket  -> strip the prefix, createSignedUrl
 *   - any other absolute URL             -> passthrough (external image)
 */

const PUBLIC_MARKER = "/storage/v1/object/public/";

// Signed URLs live 1h; refetchInterval re-signs at 50min so a rendered URL
// never outlives its token while a component stays mounted. (staleTime alone
// schedules nothing, and this app disables refetchOnWindowFocus globally —
// an interval is the only thing that actually refreshes a long-lived screen,
// and field staff keep this PWA open for entire shifts.)
const SIGN_TTL_SECONDS = 3600;
const STALE_MS = 50 * 60_000;

/**
 * Normalize a stored value to a storage path within `bucket`.
 * Returns null for URLs that aren't ours (external avatar links).
 */
export function toStoragePath(bucket: string, value: string): string | null {
  if (!/^https?:\/\//i.test(value)) return value; // already a bare path
  try {
    const u = new URL(value);
    const marker = `${PUBLIC_MARKER}${bucket}/`;
    const idx = u.pathname.indexOf(marker);
    if (idx === -1) return null;
    return decodeURIComponent(u.pathname.slice(idx + marker.length));
  } catch {
    return null;
  }
}

export async function signStorageUrl(bucket: string, value: string): Promise<string> {
  const path = toStoragePath(bucket, value);
  if (path === null) return value; // external URL — render as-is
  const { data, error } = await supabase.storage
    .from(bucket)
    .createSignedUrl(path, SIGN_TTL_SECONDS);
  if (error || !data?.signedUrl) {
    throw error ?? new Error("Could not create a signed URL");
  }
  return data.signedUrl;
}

/**
 * Batch variant for galleries — one round trip for the whole list.
 * Returns a path -> signed-url map; paths that fail to sign are omitted.
 */
export async function signStoragePaths(
  bucket: string,
  paths: string[],
): Promise<Map<string, string>> {
  const map = new Map<string, string>();
  if (paths.length === 0) return map;
  const { data, error } = await supabase.storage
    .from(bucket)
    .createSignedUrls(paths, SIGN_TTL_SECONDS);
  if (error) throw error;
  for (const item of data ?? []) {
    if (item.path && item.signedUrl && !item.error) {
      map.set(item.path, item.signedUrl);
    }
  }
  return map;
}

/**
 * Resolve a stored avatar/photo value to a renderable URL. Undefined while
 * loading or when there's nothing to show (callers fall back to initials /
 * placeholders).
 */
export function useSignedUrl(
  bucket: string,
  value: string | null | undefined,
): string | undefined {
  const { data } = useQuery({
    queryKey: ["signed-url", bucket, value],
    enabled: Boolean(value),
    staleTime: STALE_MS,
    gcTime: STALE_MS + 5 * 60_000,
    refetchInterval: STALE_MS,
    refetchIntervalInBackground: true,
    retry: 1,
    queryFn: () => signStorageUrl(bucket, value!),
  });
  return data ?? undefined;
}
